// src/components/FrameNode.tsx
import React, { useState, useRef, useCallback } from 'react';
import { NodeProps, NodeResizer, ResizeDragEvent, ResizeParams } from '@xyflow/react';

export interface FrameData {
  label: string;
  color: string;
  isReadonly: boolean;
  onLabelChange: (id: string, label: string) => void;
  onContextMenu: (e: React.MouseEvent, id: string) => void;
  onResizeEnd: (id: string, width: number, height: number, x: number, y: number) => void;
}

const FrameNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  const d = data as unknown as FrameData;
  const [editingLabel, setEditingLabel] = useState(false);
  const labelRef = useRef<HTMLInputElement>(null);

  const startEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingLabel(true);
    setTimeout(() => { labelRef.current?.focus(); labelRef.current?.select(); }, 0);
  }, []);

  const commitLabel = useCallback(() => {
    setEditingLabel(false);
    d.onLabelChange(id, labelRef.current?.value ?? d.label);
  }, [id, d]);

  return (
    <>
      <NodeResizer
        isVisible={selected && !d.isReadonly}
        minWidth={120}
        minHeight={80}
        handleStyle={{ width: 8, height: 8, background: d.color, border: 'none', borderRadius: 2 }}
        lineStyle={{ border: `1.5px dashed ${d.color}` }}
        onResizeEnd={(_: ResizeDragEvent, params: ResizeParams) => d.onResizeEnd(id, params.width, params.height, params.x, params.y)}
      />
      <div
        style={{
          width: '100%', height: '100%',
          border: `2px dashed ${d.color}`,
          borderRadius: 10,
          background: `${d.color}12`,
          position: 'relative',
          boxSizing: 'border-box',
          pointerEvents: 'all',
        }}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (!d.isReadonly) d.onContextMenu(e, id); }}
      >
        <div style={{
          position: 'absolute', top: -14, left: 12,
          background: 'var(--sb-bg, #f0f1f3)', padding: '0 8px',
        }}>
          {editingLabel ? (
            <input
              ref={labelRef}
              defaultValue={d.label}
              onBlur={commitLabel}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') labelRef.current?.blur(); }}
              style={{
                font: 'bold 11px system-ui, sans-serif',
                color: d.color, border: 'none', outline: 'none',
                background: 'transparent', padding: 0, minWidth: 80,
              }}
            />
          ) : (
            <span
              style={{ fontSize: 11, fontWeight: 700, color: d.color, cursor: 'text', userSelect: 'none' }}
              onClick={d.isReadonly ? undefined : startEdit}
            >
              {d.label || 'Frame'}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(FrameNode);
